import React, { FunctionComponent, useMemo } from 'react';

import { Document } from 'ui/types/document';
import Translate from 'ui/atoms/translate';
import DocumentModal from 'libraries/document-modal';
import { PlatformUrl } from 'ui/types/urls';
import Section from 'ui/atoms/section';
import { CreateAccountForm } from 'libraries/wizard/components/account-setup/create-account/helpers';
import Link from 'ui/atoms/link';
import Header from 'ui/atoms/header';
import {
  CreateAccountDocument,
  CreateAccountDocumentGroupType,
} from 'libraries/wizard/components/account-setup/create-account/types';
import { PersonType } from 'src/ui/types/person';

export interface AcceptCreateAccountDocumentsProps {
  documents: {
    effectaAccountSetupDocument: Document;
    effectaTermsConditions: Document;
  };
  personType: PersonType;
}

const AcceptCreateAccountDocuments: FunctionComponent<AcceptCreateAccountDocumentsProps> = ({
  documents,
  personType,
}) => {
  const acceptCheckboxes: {
    group: CreateAccountDocumentGroupType;
    checkboxes: CreateAccountDocument[];
  }[] = useMemo(
    () => [
      {
        group: 'liabilityUmbrella',
        checkboxes: [
          {
            name: 'effectaAccountSetupDocument',
            label: 'effectaAccountSetupDocument',
            documentList: [documents.effectaAccountSetupDocument],
            display: !!documents.effectaAccountSetupDocument && !!documents.effectaTermsConditions,
          },
          {
            name: 'effectaTermsConditions',
            label: 'effectaTermsConditions',
            documentList: [documents.effectaTermsConditions],
            display: !!documents.effectaAccountSetupDocument && !!documents.effectaTermsConditions,
          },
        ],
      },
      {
        group: 'platformTerms',
        checkboxes: [
          {
            name: 'termsOfService',
            label: 'termsOfService',
            documentList: [PlatformUrl.TERMS_AND_CONDITIONS, PlatformUrl.PRIVACY_POLICY],
            display: true,
          },
        ],
      },
      {
        group: 'pepAndOwnAccount',
        checkboxes: [
          { name: 'benefitingPerson', label: 'benefitingPerson', documentList: [], display: true },
          { name: 'pep', label: 'pep', documentList: [], display: true },
        ],
      },
      {
        group: 'usTax',
        checkboxes: [
          { name: 'usTax', label: 'usTaxNaturalPerson', documentList: [], display: personType === PersonType.Natural },
          { name: 'usTax', label: 'usTaxLegalPerson', documentList: [], display: personType === PersonType.Legal },
        ],
      },
    ],
    [documents, personType],
  );

  return (
    <>
      {acceptCheckboxes.map(({ group, checkboxes }) => (
        <Section key={group} spacing="small">
          <Header spacing="small" size="xsmall">
            <Translate name={`createAccount.acceptGroupType.${group}`} />
          </Header>
          {checkboxes.map(({ name, label, documentList, display }) => {
            if (!display) return null;
            return (
              <CreateAccountForm.Group name={name} key={name} required={true} spacing="small">
                <CreateAccountForm.Checkbox key={name}>
                  <Translate
                    name={`createAccount.accept.${label}`}
                    args={documentList.map((document, i) => {
                      if (typeof document === 'string') {
                        return (text) => (
                          <Link key={i} platformUrl={document} inline external>
                            {text}
                          </Link>
                        );
                      }

                      return (text) => (
                        <DocumentModal key={i} {...document}>
                          {text}
                        </DocumentModal>
                      );
                    })}
                  />
                </CreateAccountForm.Checkbox>
              </CreateAccountForm.Group>
            );
          })}
        </Section>
      ))}
    </>
  );
};

export default AcceptCreateAccountDocuments;
